import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { tv } from "tailwind-variants";

import logo from "@/assets/svgs/dataships-logo-square.svg";
import { Dropdown } from "@/components/Dropdown";
import {
  faBarsProgress,
  faBook,
  faBuildingUser,
  faCreditCard,
  faEnvelope,
  faExternalLinkSquare,
  faQuestionCircle,
  faSignOutAlt,
  faSpinnerThird,
  faUser,
} from "@fortawesome/pro-duotone-svg-icons";
import { IDropdownItem } from "@/components/Dropdown/DropdownItem";
import { KNOWLEDGE_CENTER_URL } from "@/utils/const";
import { usePostLogout } from "@/requests/hooks/user";
import { useQueryClient } from "@tanstack/react-query";
import AccountSwitcher from "@/components/AccountSwitcher";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { MobileMenu, MobileSidebar, NavLink } from "@/components/Navigation";
import useNavigationMenu from "@/navigation/useNavigationMenu";
import { logoutSuccessCallback } from "@/requests/callbacks/user";
import { useFlags } from "flagsmith/react";
import { IFeatureFlagKeys, isFeatureFlagEnabled } from "@/utils/featureFlags";
import { Badge } from "@/components/Badge";
import { getAppConfig } from "@/utils/appManager.ts";

const styles = tv({
  slots: {
    header: "fixed z-50 w-full sm:relative",
    topNav: "bg-gray-light p-20 sm:px-80 sm:py-30",
    topNavContainer:
      "mx-auto flex flex-row items-center justify-between sm:justify-normal",
    accountSwitcher: "flex-0 flex justify-start sm:mr-auto sm:flex-1 sm:py-0",
    appName:
      "hidden flex-1 items-center justify-center gap-10 !text-h5 font-semibold uppercase text-primary sm:flex",
    options: "hidden flex-1 items-center justify-end gap-20 sm:mr-auto sm:flex",
    mobileNavToggle: "flex min-w-[70px] justify-end sm:hidden",
    mobileActiveRoute:
      "mx-auto -mb-30 flex w-[33%] flex-col justify-center text-center font-semibold sm:hidden",
    mobileActiveRouteBorder: "mt-25 h-[4px] w-full rounded-t-lg bg-primary",
    bottomNav:
      "z-10 hidden flex-row justify-evenly bg-white px-80 pt-20 shadow-md transition-all duration-500 sm:flex",
    bufferFixedNav: "hidden h-50",
    mobileSidebar: "flex bg-secondary text-white sm:hidden",
  },
  variants: {
    fixedNavbar: {
      true: {
        bottomNav: "fixed left-0 top-0 w-full pt-10",
        bufferFixedNav: "hidden sm:block",
      },
    },
  },
});

const Header = () => {
  const { controlPanelHost } = getAppConfig();
  const queryClient = useQueryClient();
  const {
    mutate: logout,
    isPending,
    isSuccess: isLogoutSuccess,
  } = usePostLogout();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const flags = useFlags<IFeatureFlagKeys>([
    "web-app-mat-onboarding-enabled",
    "control-panel-switch-enabled",
  ]);

  const isControlPanelSwitchEnabled = isFeatureFlagEnabled(
    flags["control-panel-switch-enabled"],
  );

  const [activeMobileMenu, setActiveMobileMenu] =
    useState<IOpenMenuType>("NONE");

  const [offset, setOffset] = useState<number>(0);
  const navigation = useNavigationMenu();

  const {
    header,
    topNav,
    topNavContainer,
    accountSwitcher,
    appName,
    options,
    mobileNavToggle,
    mobileActiveRoute,
    mobileActiveRouteBorder,
    bottomNav,
    bufferFixedNav,
    mobileSidebar,
  } = styles({ fixedNavbar: offset >= 108 });

  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (isLogoutSuccess) logoutSuccessCallback(queryClient, navigate);
  }, [isLogoutSuccess]);

  const toggleNavigationMenu = () =>
    setActiveMobileMenu(activeMobileMenu =>
      activeMobileMenu === "NAVIGATION" ? "NONE" : "NAVIGATION",
    );

  const toggleAccountSwitcherMenu = () =>
    setActiveMobileMenu(activeMobileMenu =>
      activeMobileMenu === "ACCOUNT_SWITCHER" ? "NONE" : "ACCOUNT_SWITCHER",
    );

  const toggleMobileSidebarMenu = () =>
    setActiveMobileMenu(activeMobileMenu =>
      activeMobileMenu === "MOBILE_SIDEBAR" ? "NONE" : "MOBILE_SIDEBAR",
    );

  const renderNavigation = () =>
    navigation
      .filter(({ hidden, isMobileOnly }) => !!!hidden?.() && !isMobileOnly)
      .map(({ url, external, newTab, title, locked, sidebar }, index) => {
        const firstAvailableSidebarItem = sidebar.find(({ hidden, locked }) => {
          return !!!hidden?.() && !!!locked?.();
        });

        const topNavUrl = !!firstAvailableSidebarItem
          ? `${url}${firstAvailableSidebarItem.url}`
          : url;

        return (
          <NavLink
            key={index}
            isActive={pathname.startsWith(url)}
            isLocked={!!locked?.()}
            to={topNavUrl}
            external={!!external}
            newTab={!!newTab}
            minimize={offset >= 108}>
            {title}
          </NavLink>
        );
      });

  const getActiveNavItem = () =>
    navigation.find(({ url }) => pathname.includes(url));

  const renderHelpDropdown = () => {
    const items: IDropdownItem[] = [
      {
        leadingIcon: faBook,
        text: t("layouts.navigation.header.helpDropdown.knowledgeCenter"),
        onClick: () => window.open(KNOWLEDGE_CENTER_URL, "_blank"),
      },
      {
        leadingIcon: faEnvelope,
        text: t("layouts.navigation.header.helpDropdown.contactSupport"),
        onClick: () => navigate("/support"),
      },
    ];

    return (
      <Dropdown
        button={{
          icon: faQuestionCircle,
        }}
        items={items}
      />
    );
  };

  const renderProfileDropdown = () => {
    const isWebAppMatOnboardingEnabled = isFeatureFlagEnabled(
      flags["web-app-mat-onboarding-enabled"],
    );

    const items: IDropdownItem[] = isWebAppMatOnboardingEnabled
      ? [
          {
            leadingIcon: faBuildingUser,
            text: t("layouts.navigation.header.profileDropdown.companyProfile"),
            onClick: () => navigate("/profile/company"),
          },
          {
            leadingIcon: faCreditCard,
            text: t("layouts.navigation.header.profileDropdown.billing"),
            onClick: () => navigate("/profile/billing"),
          },
          {
            leadingIcon: faBarsProgress,
            text: t("layouts.navigation.header.profileDropdown.userManagement"),
            onClick: () => navigate("/profile/user-management"),
          },
        ]
      : [
          {
            leadingIcon: faSignOutAlt,
            hoverColor: "red",
            text: t("generic.logOut"),
            onClick: logout,
          },
        ];

    return (
      <Dropdown
        testId="profile-dropdown"
        button={{
          icon:
            isPending && !isWebAppMatOnboardingEnabled
              ? faSpinnerThird
              : faUser,
          iconProps: {
            spin: isPending && !isWebAppMatOnboardingEnabled,
          },
        }}
        items={items}
      />
    );
  };

  const renderLogoutDropdown = () => {
    const isWebAppMatOnboardingEnabled = isFeatureFlagEnabled(
      flags["web-app-mat-onboarding-enabled"],
    );

    if (!isWebAppMatOnboardingEnabled) return;

    const items: IDropdownItem[] = [
      {
        leadingIcon: faSignOutAlt,
        hoverColor: "red",
        text: t("generic.logOut"),
        onClick: logout,
      },
    ];

    return (
      <Dropdown
        testId="logout-dropdown"
        button={{
          icon: isPending ? faSpinnerThird : faSignOutAlt,
          iconProps: {
            spin: isPending,
          },
        }}
        items={items}
      />
    );
  };

  return (
    <header className={header()} role="navigation">
      <div className={topNav()}>
        <div className={topNavContainer()}>
          <div className={accountSwitcher()}>
            <AccountSwitcher
              activeOpenMenu={activeMobileMenu}
              toggleMenu={toggleAccountSwitcherMenu}
            />
          </div>
          <div
            className={mobileActiveRoute()}
            data-testid="mobile-active-nav-link">
            {getActiveNavItem()?.title}
            <div className={mobileActiveRouteBorder()} />
          </div>
          <Link to="/" className={appName()}>
            <img src={logo} alt={"Logo"} />
            {t("layouts.navigation.header.controlPanel")}
          </Link>
          <div className={options()}>
            {isControlPanelSwitchEnabled && (
              <a href={`${controlPanelHost}/analytics/dashboard`}>
                <Badge
                  variant="success"
                  icon={faExternalLinkSquare}
                  iconPosition="end">
                  <b>NEW:</b> Control Panel
                </Badge>
              </a>
            )}
            {renderHelpDropdown()}
            {renderProfileDropdown()}
            {renderLogoutDropdown()}
          </div>
          <div className={mobileNavToggle()} onClick={toggleNavigationMenu}>
            <FontAwesomeIcon
              fixedWidth
              icon={activeMobileMenu === "NAVIGATION" ? faTimes : faBars}
            />
          </div>
        </div>
      </div>
      <div className={bottomNav()}>{renderNavigation()}</div>
      <div className={bufferFixedNav()} />
      {activeMobileMenu === "NAVIGATION" && (
        <MobileMenu
          activeMenuItem={getActiveNavItem()}
          toggleOpen={toggleNavigationMenu}
        />
      )}
      <div className={mobileSidebar()}>
        <MobileSidebar
          activeMenuItem={getActiveNavItem()}
          openMenu={activeMobileMenu}
          toggleOpen={toggleMobileSidebarMenu}
        />
      </div>
    </header>
  );
};

export default Header;
